import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnsubscribeForm = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/newsletter/unsubscribe`, { email });
      setDone(true);
      toast.success(res.data.message);
    } catch {
      toast.error("Erreur, veuillez réessayer.");
    }
  };

  if (done) {
    return (
      <p className="flex items-center gap-2 text-sm text-[#0F4C4C] font-medium" data-testid="unsubscribe-success">
        <CheckCircle2 className="w-4 h-4" /> Votre demande de désinscription a bien été prise en compte.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md" data-testid="unsubscribe-form">
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
        placeholder="votre@email.com" data-testid="unsubscribe-email-input"
        className="flex-1 bg-white border border-[#DAD5C8] px-4 py-2.5 text-sm"
      />
      <button type="submit" data-testid="unsubscribe-submit-btn"
        className="bg-[#0F4C4C] text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#1A6B6B] transition-colors">
        Me désinscrire
      </button>
    </form>
  );
};

export default function PrivacyPage() {
  const resetCookieConsent = () => {
    localStorage.removeItem("vf-cookie-consent");
    const host = window.location.hostname;
    const parent = "." + host.split(".").slice(-2).join(".");
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name === "_ga" || name.startsWith("_ga_")) {
        [host, parent, ""].forEach((d) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
        });
      }
    });
    if (window.VF_GA_ID) window[`ga-disable-${window.VF_GA_ID}`] = true;
    toast.success("Préférences réinitialisées. Le bandeau cookies s'affichera à nouveau.");
    setTimeout(() => window.location.reload(), 1200);
  };

  return (
    <div className="min-h-screen bg-[#F6F4EC]" data-testid="privacy-page">
      <Toaster position="top-right" richColors />
      <header className="bg-[#0F4C4C]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="font-display text-xl font-bold text-white">VF ACTIONS</Link>
            <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#F5B800] transition-colors">
              <ArrowLeft size={16} /> Retour au site
            </Link>
          </div>
          <div className="py-12">
            <h1 className="font-display text-4xl font-bold text-white">Politique de confidentialité</h1>
            <p className="text-white/70 text-sm mt-3">Conforme au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16 space-y-10">
        <section data-testid="privacy-responsable">
          <h2 className="font-display text-xl font-bold text-[#142828] mb-3">1. Responsable du traitement</h2>
          <p className="text-sm text-[#516969] leading-relaxed whitespace-pre-line">{`VF ACTIONS, SASU
SIREN : 988 144 622 — R.C.S. Brest
12 Rue Jacques Andrieux, 29270 Carhaix-Plouguer, France
Contact données personnelles : vferec29@gmail.com`}</p>
        </section>

        <section data-testid="privacy-donnees">
          <h2 className="font-display text-xl font-bold text-[#142828] mb-3">2. Données collectées et finalités</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-[#DAD5C8] bg-white">
              <thead className="bg-[#0F4C4C] text-white text-left">
                <tr>
                  <th className="p-3 font-display">Traitement</th>
                  <th className="p-3 font-display">Données</th>
                  <th className="p-3 font-display">Base légale</th>
                  <th className="p-3 font-display">Conservation</th>
                </tr>
              </thead>
              <tbody className="text-[#516969]">
                <tr className="border-t border-[#DAD5C8]">
                  <td className="p-3 font-medium text-[#142828]">Formulaire de contact</td>
                  <td className="p-3">Nom, email, structure, message</td>
                  <td className="p-3">Mesures précontractuelles / consentement</td>
                  <td className="p-3">3 ans après le dernier contact</td>
                </tr>
                <tr className="border-t border-[#DAD5C8]">
                  <td className="p-3 font-medium text-[#142828]">Newsletter</td>
                  <td className="p-3">Email</td>
                  <td className="p-3">Consentement</td>
                  <td className="p-3">Jusqu'à désinscription</td>
                </tr>
                <tr className="border-t border-[#DAD5C8]">
                  <td className="p-3 font-medium text-[#142828]">Mesure d'audience</td>
                  <td className="p-3">Données de navigation anonymisées</td>
                  <td className="p-3">Consentement (bandeau cookies)</td>
                  <td className="p-3">Consentement valable 6 mois, cookies 13 mois max</td>
                </tr>
                <tr className="border-t border-[#DAD5C8]">
                  <td className="p-3 font-medium text-[#142828]">Prise de rendez-vous</td>
                  <td className="p-3">Gérée par Cal.com (politique propre)</td>
                  <td className="p-3">Mesures précontractuelles</td>
                  <td className="p-3">Selon la politique Cal.com</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="privacy-destinataires">
          <h2 className="font-display text-xl font-bold text-[#142828] mb-3">3. Destinataires et sous-traitants</h2>
          <p className="text-sm text-[#516969] leading-relaxed">Vos données ne sont jamais vendues ni cédées. Elles sont accessibles uniquement à VF ACTIONS et à ses sous-traitants techniques : hébergeur du site (infrastructure cloud, données hébergées en Europe) et prestataire d'envoi d'emails (Resend). Lorsque des transferts hors Union européenne sont nécessaires, ils sont encadrés par les clauses contractuelles types de la Commission européenne.</p>
        </section>

        <section data-testid="privacy-droits">
          <h2 className="font-display text-xl font-bold text-[#142828] mb-3">4. Vos droits</h2>
          <p className="text-sm text-[#516969] leading-relaxed">Conformément au RGPD, vous disposez des droits d'accès, de rectification, d'effacement, d'opposition, de limitation du traitement et de portabilité de vos données. Pour les exercer, écrivez-nous à <a href="mailto:vferec29@gmail.com" className="text-[#0F4C4C] font-medium underline underline-offset-2">vferec29@gmail.com</a> — nous répondons sous un mois maximum. Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#0F4C4C] font-medium underline underline-offset-2">www.cnil.fr</a>).</p>
        </section>

        <section id="cookies" data-testid="privacy-cookies">
          <h2 className="font-display text-xl font-bold text-[#142828] mb-3">5. Cookies</h2>
          <p className="text-sm text-[#516969] leading-relaxed mb-4">Le site dépose uniquement des cookies de mesure d'audience (Google Analytics, IP anonymisée), et seulement après votre consentement explicite via le bandeau. Aucun cookie publicitaire n'est utilisé. Votre refus n'altère en rien votre navigation. Votre choix est conservé 6 mois et modifiable à tout moment :</p>
          <button onClick={resetCookieConsent} data-testid="privacy-cookie-reset-btn"
            className="bg-[#F5B800] text-[#082A2A] px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#FFD54F] transition-colors">
            Gérer mes préférences cookies
          </button>
        </section>

        <section id="desinscription" data-testid="privacy-unsubscribe">
          <h2 className="font-display text-xl font-bold text-[#142828] mb-3">6. Désinscription de la newsletter</h2>
          <p className="text-sm text-[#516969] leading-relaxed mb-4">Vous pouvez retirer votre consentement à tout moment. Saisissez votre adresse email pour être immédiatement supprimé(e) de notre liste :</p>
          <UnsubscribeForm />
        </section>

        <p className="text-xs text-[#516969] italic">Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</p>
      </main>

      <footer className="bg-[#082A2A] py-8">
        <p className="text-center text-xs text-white/50">© {new Date().getFullYear()} VF ACTIONS — <Link to="/mentions-legales" className="hover:text-[#F5B800]">Mentions légales</Link></p>
      </footer>
    </div>
  );
}
